// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminBilling\AdminBilling.jsx
// Название: Admin Billing - PSP Payments
// Версия: 1.1.0 - Delete pending
// Дата: 19.12.2025

import React, { useState, useEffect } from 'react';
import './AdminBilling.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const STATUS_LABELS = {
  'pending': { label: 'Ожидание', icon: '⏳', color: 'orange' },
  'completed': { label: 'Оплачено', icon: '✅', color: 'green' },
  'failed': { label: 'Ошибка', icon: '❌', color: 'red' },
  'refunded': { label: 'Возврат', icon: '↩️', color: 'gray' },
};

const AdminBilling = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, totalEur: 0, totalDar: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL + '/api/admin/billing/payments', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      const data = await response.json();
      
      if (data.success) {
        const list = data.data || [];
        setPayments(list);
        calculateStats(list);
      } else {
        setPayments([]);
        setStats({ total: 0, completed: 0, totalEur: 0, totalDar: 0 });
      }
    } catch (err) {
      console.error('Error:', err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (list) => {
    const completed = list.filter(p => p.status === 'completed');
    setStats({
      total: list.length,
      completed: completed.length,
      totalEur: completed.reduce((sum, p) => sum + parseFloat(p.amount_eur || 0), 0),
      totalDar: completed.reduce((sum, p) => sum + parseFloat(p.amount_dar || 0), 0)
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить pending платёж #' + id + '?')) return;
    
    try {
      setDeleting(id);
      const response = await fetch(API_URL + '/api/admin/billing/payments/' + id, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      const data = await response.json();
      
      if (data.success) {
        setPayments(prev => prev.filter(p => p.id !== id));
        calculateStats(payments.filter(p => p.id !== id));
      } else {
        alert('Ошибка: ' + (data.error || 'Не удалось удалить'));
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const filteredPayments = payments.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    return true;
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => STATUS_LABELS[status] || { label: status, icon: '❓', color: 'gray' };

  return (
    <div className="admin-billing">
      <div className="admin-page-header">
        <h1 className="admin-page-title">💳 Billing (PSP Платежи)</h1>
        <div className="admin-page-actions">
          <button className="admin-btn admin-btn-primary" onClick={fetchPayments}>🔄 Обновить</button>
        </div>
      </div>

      <div className="billing-stats">
        <div className="billing-stat">
          <span className="billing-stat-value">{stats.total}</span>
          <span className="billing-stat-label">Всего платежей</span>
        </div>
        <div className="billing-stat green">
          <span className="billing-stat-value">{stats.completed}</span>
          <span className="billing-stat-label">Успешных</span>
        </div>
        <div className="billing-stat blue">
          <span className="billing-stat-value">{stats.totalEur.toFixed(2)} €</span>
          <span className="billing-stat-label">Сумма EUR</span>
        </div>
        <div className="billing-stat purple">
          <span className="billing-stat-value">{stats.totalDar.toFixed(0)} DAR</span>
          <span className="billing-stat-label">Выдано DAR</span>
        </div>
      </div>

      <div className="billing-filter">
        <label>Статус:</label>
        <div className="billing-filter-buttons">
          <button 
            className={'billing-filter-btn' + (statusFilter === 'all' ? ' active' : '')}
            onClick={() => setStatusFilter('all')}
          >
            📋 Все
          </button>
          {Object.entries(STATUS_LABELS).map(([key, info]) => (
            <button
              key={key}
              className={'billing-filter-btn' + (statusFilter === key ? ' active' : '') + ' ' + info.color}
              onClick={() => setStatusFilter(key)}
            >
              {info.icon} {info.label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">Загрузка...</div>
        ) : filteredPayments.length === 0 ? (
          <div className="admin-empty">
            <span style={{fontSize: '48px', display: 'block', marginBottom: '15px'}}>💳</span>
            <p>Платежей не найдено</p>
          </div>
        ) : (
          <div className="billing-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Бизнес</th>
                  <th>Сумма EUR</th>
                  <th>Сумма DAR</th>
                  <th>Статус</th>
                  <th>Провайдер</th>
                  <th>Дата</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map(p => {
                  const statusInfo = getStatusInfo(p.status);
                  return (
                    <tr key={p.id}>
                      <td>#{p.id}</td>
                      <td>{p.business_email || ('Business #' + p.business_id)}</td>
                      <td><strong>{parseFloat(p.amount_eur || 0).toFixed(2)} €</strong></td>
                      <td><strong>{(parseFloat(p.amount_dar) || 0).toFixed(2)} DAR</strong></td>
                      <td>
                        <span className={'billing-status ' + statusInfo.color}>
                          {statusInfo.icon} {statusInfo.label}
                        </span>
                      </td>
                      <td>{p.provider || 'mock'}</td>
                      <td>{formatDate(p.created_at)}</td>
                      <td>
                        {p.status === 'pending' && (
                          <button
                            className="admin-btn admin-btn-sm admin-btn-danger"
                            onClick={() => handleDelete(p.id)}
                            disabled={deleting === p.id}
                            title="Удалить pending платёж"
                          >
                            {deleting === p.id ? '...' : '🗑️'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBilling;